import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

let rumInitialized = false
let logsInitialized = false

const parseNumberEnv = (raw: unknown, fallback: number): number => {
  if (typeof raw !== 'string' || raw.trim() === '') return fallback
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : fallback
}

const getDatadogEnv = (): string | undefined => {
  const env = import.meta.env.VITE_DD_RUM_ENV
  return env === 'production' ? 'live' : (env as string | undefined)
}

export const initDatadog = (): void => {
  if (rumInitialized && logsInitialized) return

  const applicationId = import.meta.env.VITE_DD_RUM_APPLICATION_ID
  const clientToken = import.meta.env.VITE_DD_RUM_CLIENT_TOKEN

  if (!applicationId || !clientToken) {
    console.warn(
      '[datadog] VITE_DD_RUM_APPLICATION_ID or VITE_DD_RUM_CLIENT_TOKEN missing; skipping init.'
    )
    return
  }

  const site = (import.meta.env.VITE_DD_RUM_SITE ?? 'datadoghq.com') as string
  const env = getDatadogEnv()
  const version = (import.meta.env.VITE_BUILD_VERSION as string | undefined) || undefined

  if (!rumInitialized) {
    try {
      datadogRum.init({
        applicationId: applicationId as string,
        clientToken: clientToken as string,
        site,
        service: 'foodstorm-kds',
        env,
        version,
        sessionSampleRate: parseNumberEnv(import.meta.env.VITE_DD_RUM_SAMPLE_RATE, 100),
        sessionReplaySampleRate: parseNumberEnv(
          import.meta.env.VITE_DD_RUM_SESSION_REPLAY_SAMPLE_RATE,
          0
        ),
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: sanitizeRumEvent,
      })
      rumInitialized = true
      datadogRum.setGlobalContextProperty('device.type', 'kds')
      datadogRum.setGlobalContextProperty('app.name', 'kds')
    } catch (error) {
      console.warn('[datadog] RUM initialization failed; continuing without RUM.', error)
    }
  }

  if (!logsInitialized) {
    try {
      datadogLogs.init({
        clientToken: clientToken as string,
        site,
        service: 'foodstorm-kds',
        env,
        version,
        forwardErrorsToLogs: true,
      })
      logsInitialized = true
      datadogLogs.setGlobalContextProperty('device.type', 'kds')
      datadogLogs.setGlobalContextProperty('app.name', 'kds')
    } catch (error) {
      console.warn('[datadog] Logs initialization failed; continuing without Logs.', error)
    }
  }
}

// oidc-client-ts's onSigninCallback rewrites /callback?code=...&state=... to /
// on mount, but Datadog RUM can emit its first view or resource event with the
// original URL before that rewrite lands. Strip the query string from any
// event whose URL is still on the callback path so the authorization code and
// state never reach Datadog.
const OIDC_CALLBACK_PATH = '/callback'

const stripCallbackQuery = (url: string | undefined): string | undefined => {
  if (!url) return url
  try {
    const parsed = new URL(url)
    if (parsed.pathname === OIDC_CALLBACK_PATH) {
      parsed.search = ''
      parsed.hash = ''
      return parsed.toString()
    }
  } catch {
    // Ignore malformed URLs — better to drop the redaction than to throw
    // inside a Datadog event pipeline callback.
  }
  return url
}

const sanitizeRumEvent = (event: {
  type?: string
  view?: { url?: string }
  resource?: { url?: string }
}): boolean => {
  if (event.view?.url) {
    event.view.url = stripCallbackQuery(event.view.url) ?? event.view.url
  }
  if (event.resource?.url) {
    event.resource.url = stripCallbackQuery(event.resource.url) ?? event.resource.url
  }
  return true
}

export interface DeviceContext {
  deviceUid?: string
  clientId?: string
  siteUid?: string
  departmentUid?: string
  departmentName?: string
}

// Emit both canonical dot-namespaced tags (org convention) and the raw
// ClientUid / KdsDeviceUid claim names so Datadog facets configured against
// either form group correctly.
const CONTEXT_FIELD_MAP: Array<[keyof DeviceContext, string]> = [
  ['deviceUid', 'device.uid'],
  ['deviceUid', 'KdsDeviceUid'],
  ['clientId', 'client.id'],
  ['clientId', 'ClientUid'],
  ['siteUid', 'site.uid'],
  ['departmentUid', 'department.uid'],
  ['departmentName', 'department.name'],
]

export const setDeviceContext = (context: DeviceContext): void => {
  if (!rumInitialized && !logsInitialized) return

  for (const [sourceKey, tagName] of CONTEXT_FIELD_MAP) {
    const value = context[sourceKey]
    if (!value) continue

    // Isolate each SDK call: setDeviceContext runs inside loadConfig's
    // resolution path, so a telemetry throw must not reject the config load
    // and strand KDS on the error screen.
    if (rumInitialized) {
      try {
        datadogRum.setGlobalContextProperty(tagName, value)
      } catch (error) {
        console.warn('[datadog] failed to set RUM context property.', error)
      }
    }
    if (logsInitialized) {
      try {
        datadogLogs.setGlobalContextProperty(tagName, value)
      } catch (error) {
        console.warn('[datadog] failed to set Logs context property.', error)
      }
    }
  }
}

type KdsTokenClaims = {
  kdsDeviceUid?: string
  clientId?: string
  siteId?: string
  departmentId?: string
}

export const setDeviceContextFromToken = (accessToken: string): void => {
  try {
    const encodedPayload = accessToken.split('.')[1]
    if (!encodedPayload) return

    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const claims = JSON.parse(atob(paddedBase64)) as KdsTokenClaims

    setDeviceContext({
      deviceUid: claims.kdsDeviceUid,
      clientId: claims.clientId,
      siteUid: claims.siteId,
      departmentUid: claims.departmentId,
    })
  } catch {
    // The token is only decoded for telemetry tags. Authentication remains the
    // source of truth, so malformed telemetry context must not block the app.
  }
}

export const trackError = (error: unknown, context: Record<string, unknown> = {}): void => {
  const normalizedError = error instanceof Error ? error : new Error(String(error))

  // Isolate both SDKs: trackError runs from inside error-handling paths
  // (config load .catch, error boundary onError) where re-throwing would
  // convert a handled error into an unhandled rejection.
  if (rumInitialized) {
    try {
      datadogRum.addError(normalizedError, context)
    } catch (datadogError) {
      console.warn('[datadog] failed to add RUM error.', datadogError)
    }
  }

  if (logsInitialized) {
    try {
      datadogLogs.logger.error(normalizedError.message, context, normalizedError)
    } catch (datadogError) {
      console.warn('[datadog] failed to add error log.', datadogError)
    }
  }
}

export const __resetForTests = (): void => {
  rumInitialized = false
  logsInitialized = false
}
